<template src="./templates/index.html" />

<script type="text/ecmascript-6">
import isArray from 'lodash/isArray'
import isEqual from 'lodash/isEqual'
import isEmpty from 'lodash/isEmpty'
import { UserDisplayName } from 'src/utils/helper/user'
import { mapActions, mapGetters } from 'vuex'
import Logo from 'components/icons/logo/'
import { AUTH_LOGOUT_URL } from 'src/utils/constants/auth0'
import SubmissionMethods from 'src/pages/submissions/script/common.methods'
import Misc, { scrollToSection } from 'src/utils/helper/misc'
import { MdIconCompany, MdIconResources, MdIconComplianceExecution, MdTooltip } from '@antiqua-dls/vue-components'
import { adobeCjaEventEmitter } from '../../../../../modules/adobe-cja/utils'
import { CJA_EVENTS, LINK_TYPES, LINK_CATEGORIES } from '../../../../../modules/adobe-cja/constants'
import StarIcon from './icons/star-icon'
import FoldersIcon from './icons/folders-icon'
import UploadIcon from './icons/upload-icon'
import RectangleTag from './icons/rectangle-tag'
import NotificationBell from './icons/notification-bell'
import BundleIcon from './icons/bundle-icon'
import { USER_MANUAL_URL } from 'src/utils/constants/common'
import SignAgreementModal from 'components/general/small-popup-info-modal'
import { MdTypography } from '@antiqua-dls/vue-components'

const {
  accountsUrl,
  myFontsUrl,
  MTFontsUrl
} = Misc.getAuth0CredDetails()

export default {
  name: 'Navigation',

  components: {
    Logo,
    StarIcon,
    FoldersIcon,
    UploadIcon,
    RectangleTag,
    NotificationBell,
    BundleIcon,
    MdIconCompany,
    MdIconResources,
    MdIconComplianceExecution,
    MdTooltip,
    SignAgreementModal,
    MdTypography,
  },

  data() {
    return {
      hamMenuDrawer: false,
      subMenuoption: false,
      menu: false,
      removeSubscriptions: null,
      settingsFeature: false,
      permissionArr: [],
      imageURL:'',
      USER_MANUAL_URL,
      showAgreementModal: false,
      myFontsUrl,
      MTFontsUrl
    }
  },


  computed: {
    user: {
      get() {
        return this.$store.getters['auth/user']
      },
      set(user) {
        this.user.name = user.name
        this.user.displayName = user.displayName
      }
    },
    ...mapGetters('auth', {
      hasRole: 'hasRole',
      userPermissions: 'getPermissionsArr',
      getLogoutState: 'getLogoutState',
      isContractLocked: 'isContractLocked'
    }),
    ...mapGetters('profile', {
      sessionFoundryId: ['sessionFoundryId'],
      defaultFoundry: ['defaultFoundry'],
      foundries: ['foundries'],
      returnUrl: ['getReturnUrl'],
      foundryBannerURL: ['getBannerURL'],
      foundryLogoURL: ['getLogoURL'],
    }),
    showMenuBox: {
      get() {
        return this.menu
      },
      set() {
        this.menu = !this.menu
      }
    },
    isLogin: {
      get() {
        return this.getCurrentPage() === 'login'
      }
    },
    isValidNavigation: {
      get() {
        let navigations = ['get_started', 'sign_up', 'application_status', 'success_signup', 'success_signup_status']
        return !navigations.includes(this.getCurrentPage())
      }
    },
    checkNewUser: {
      get() {
        if (this.hasRole(['foundryAdmin', 'foundryUser', 'foundryFontManager', 'foundrySalesManager'])) {
          return true
        }
        return this.hasRole(['foundrySupport', 'bulkIngestionUser']);

      }
    },
    loggedEmail() {
      let username = this.user.name.split('@')[0]
      let domain = this.user.name.split('@')[1]
      if (username.length > 9) {
        return `${username.slice(0, 9)}...@${domain}`
      }
      return this.user.name
    },
    showUserProfile() {
      const currentUser = this.user
      if (this.hasRole(['foundrySupport'])) {
        return true
      }
      return !((currentUser.roles.length === 0) && currentUser.foundries.length === 0 && currentUser.applicationStatus !== 'exists')
    },
    getImpersonateUser() {
      if (this.user.impersonate) {
        return this.user.impersonate
      }
      const fetchFSValue = JSON.parse(window.localStorage.getItem('FS-Impersonate'))
      return !!(fetchFSValue && !this.getLogoutState)
    },
    ...mapGetters('sideNavigation', ['showHamIcon']),
    filteredFoundryContract: {
      get() {
        const foundry = this.foundries.find(f => f.id === this.sessionFoundryId);
        return foundry && (foundry?.contractSummary === 'UFDA 2' || foundry?.contractSummary === 'Monotype IP');
      }
    },
    hasFoundryProfilePermission() {
      return Misc.foundryHasModulePermission('Foundry Profile', this.foundries, this.sessionFoundryId)
    },
    hasAgreementsPermission() {
      return Misc.foundryHasModulePermission('agreements', this.foundries, this.sessionFoundryId)
    }
  },

  created() {
    this.setSubscriptions()
    document.addEventListener('click', this.handleOutsideClick);
  },

  updated() {
    const storedImageUrl = localStorage.getItem('loginPic')
    if (storedImageUrl) {
      this.imageURL = storedImageUrl
    }
  },

  beforeUnmount() {
    if (this.removeSubscriptions) {
      this.removeSubscriptions()
    }
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    ...SubmissionMethods,
    ...Misc,
    triggerCjaEvent (linkName, linkCategory = LINK_CATEGORIES.NAVIGATION, linkType = LINK_TYPES.BUTTON, linkSection = '') {
      adobeCjaEventEmitter({
        event: CJA_EVENTS.LINK_CLICK,
        links: {
          linkCategory: linkCategory,
          linkSection: linkSection || window?.pageInfoGlobal?.pageInfo?.pageSection || '',
          linkName: linkName,
          linkType: linkType
        }
      })
    },
    scrollToTop() {
      window.scrollTo(0, 0)
    },
    getProfileUrl() {
      return accountsUrl
    },
    getDisplayName() {
      const name = this.user.displayName.trim() !== ''
        ? this.user.displayName
        : this.user.name
      return UserDisplayName.get(name)
    },
    getCurrentPage() {
      const routeName = this.$route.name
      return routeName
        ? this.$sanitize(routeName)
        : false
    },
    isValidPermissionArr() {
      if (!isEmpty(this.userPermissions)) {
        this.permissionArr = this.userPermissions || JSON.parse(window.localStorage.getItem('permissionsArr'))
      }
      return isArray(this.permissionArr) && !isEqual(this.permissionArr.length, 0)
    },
    getNavigationTabsPermission(tabName) {
      const permissions = JSON.parse(window.localStorage.getItem('permissionsArr'))
      return permissions.includes(tabName)
    },
    setSubscriptions() {
      this.removeSubscriptions = this.$store.subscribe((mutation, state) => {
        if (mutation.type === 'profile/set') {
          this.$store.dispatch('auth/checkAuth')
            .then(() => {
              this.user = this.$store.getters['auth/user'] // get refreshed display name
            })
            .catch(() => { })
        }
      })
    },
    setMenu () {
      this.menu = false
    },
    handleLogOut() {
      /*
       * Handle LogOut Button Click
       */
      this.triggerCjaEvent('log out', LINK_CATEGORIES.USER_PROFILE, LINK_TYPES.LINK_TEXT)
      this.menu = false
      window.location.href = AUTH_LOGOUT_URL
    },
    createNewSubmission() {
      this.triggerCjaEvent('submit fonts')
      if(this.$store.getters['submissions/getIsSubmissionInProgress']){
        this.$events.$emit('customPopupSubmissions', 'createNewSubmission')
        return
      }

      return new Promise(resolve => {
        const sessionId = window.localStorage.getItem('sessionFoundryId')
        const foundryData = this.setFoundryForModuleOnNavigation(this.defaultFoundry, this.foundries, 'submissions', sessionId)
        window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(this.foundries)))
        this.$store.dispatch('auth/setPermissionsArr', Misc.getPermissionsArr(this.foundries))
        this.$store.dispatch('auth/setSessionFoundry', { id: foundryData.foundryId, name: foundryData.foundryName }).then(() => {
          this.createSubmission().then(submission => {
            this.$router.push({
              name: 'submissions_id',
              params: {
                id: submission?.id
              }
            })
            resolve(submission)
          })
        })
      })
    },
    createNewBundle() {
      if(this.$store.getters['submissions/getIsSubmissionInProgress']){
        this.$events.$emit('customPopupSubmissions', 'createNewBundle')
        return
      }

      return new Promise(resolve => {
        const sessionId = window.localStorage.getItem('sessionFoundryId')
        const foundryData = this.setSessionFoundryForBundleModuleOnNavigation(this.defaultFoundry, this.foundries, 'bundles', sessionId, false)
        window.localStorage.setItem('permissionsArr', JSON.stringify(Misc.getPermissionsArr(this.foundries)))
        this.$store.dispatch('auth/setPermissionsArr', Misc.getPermissionsArr(this.foundries))
        this.$store.dispatch('auth/setSessionFoundry', { id: foundryData.foundryId, name: foundryData.foundryName }).then(() => {
          this.$store.dispatch('bundle/createBundle', { foundries: [{ name: foundryData.foundryName, id: foundryData.foundryId }] })
            .then(res => {
              this.$router.push({
                name: 'BundleSubmissionDraft',
                params: {
                  id: res.bundle.id
                }
              })
            })
        })
      })
    },
    createNewPromotionRedirection() {
      this.$router.push({ path: '/promos/new-promo' })
    },
    navigateToVideos() {
      this.triggerCjaEvent('videos and articles')
      if (this.filteredFoundryContract) {
        this.$store.commit('dashboard/setScrollToArticles', true);
        
        this.$router.push({ 
          name: 'dashboard'
        });

        this.$nextTick(() => {
          scrollToSection('.fou-dashboard-page-articles');
        });
      } else {
        this.showAgreementModal = !this.showAgreementModal;
      }
    },
    brandYourProfile() {
      this.triggerCjaEvent('brand your profile')
      const sessionFoundryId = window.localStorage.getItem('sessionFoundryId');
      this.$router.push(`/edit-foundry/${sessionFoundryId}`);
    },
    navigateToFoundryHelpCenter() {
      this.triggerCjaEvent('foundry help center')
    },
    beamerCJAEvent() {
      this.triggerCjaEvent('beamer', LINK_CATEGORIES.NAVIGATION, LINK_TYPES.ICON)
    },
    toggleMenu() {
      this.triggerCjaEvent('My profile', LINK_CATEGORIES.USER_PROFILE, LINK_TYPES.ICON)
      this.menu = !this.menu;
    },
    myAccountCJAEvent() {
      this.triggerCjaEvent('my account', LINK_CATEGORIES.USER_PROFILE, LINK_TYPES.LINK_TEXT)
    },
    monotypeDashboardCJAEvent() {
      this.triggerCjaEvent('Go to MTF', LINK_CATEGORIES.MTF_LINK, LINK_TYPES.BUTTON, 'My Profile')
    },
    navigateToAgreementSection() {
      this.$router.push({ name: 'contract' });
    },  
    handleOutsideClick(event) {
      const modal = this.$refs.signAgreementModal?.$el;
      const videosAndArticlesBtnId = document.getElementById('videosAndArticlesBtn');
      if (this.showAgreementModal && !modal?.contains(event.target) && !videosAndArticlesBtnId?.contains(event.target)) {
        this.showAgreementModal = false;
      }
    },
    showMonotypeFonts() {
        const hasMtfAccess =  window.localStorage.getItem('hasMTFAccess')
        return hasMtfAccess === 'true'
    },
    ...mapActions('sideNavigation', ['changeHamIconStatus']),
  },
}
</script>

<style lang="scss" src="./stylesheets/index.scss" module></style>
